import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { MiPlaceholderArea } from "@miplatform/components";
import {
  IInspectionTemplatePayload,
  IOrderReason,
  ITransferListItem,
} from "@miplatform/types";
import { simpleSort } from "@miplatform/utils";
import { TransferList } from "@mibuildweb/components";
import { useOrderReasons } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";

export const InspectionTemplateRescheduleReasonsTab = ({
  form,
  extraTabProps,
}: InspectionTemplateRescheduleReasonsTabProps) => {
  const theme = useTheme();
  const { getRescheduleReasons, rescheduleReasons: allRescheduleReasons } =
    useOrderReasons();
  const { isReadOnly } = extraTabProps || {};

  const { setValue, watch } = form;
  const rescheduledReasonIds = watch("rescheduledReasonIds");

  const rescheduleReasonsMap = useMemo(() => {
    const map: Record<string, IOrderReason> = {};
    allRescheduleReasons.forEach((reason) => {
      map[reason.id] = reason;
    });
    return map;
  }, [allRescheduleReasons]);

  useEffect(() => {
    getRescheduleReasons();
  }, []);

  const availableRescheduleReasons = useMemo(
    () =>
      allRescheduleReasons
        .sort((a, b) => simpleSort(a.label, b.label))
        .map((i) => ({
          key: i.id,
          title: i.label,
        })) as ITransferListItem[],
    [allRescheduleReasons]
  );

  const selectedRescheduleReasons = useMemo(
    () =>
      rescheduledReasonIds
        ?.map((id) => rescheduleReasonsMap[id])
        .filter((reason): reason is NonNullable<typeof reason> => !!reason)
        .sort((a, b) => simpleSort(a.label, b.label))
        .map((i) => ({
          key: i.id,
          title: i.label,
        })) || [],
    [rescheduledReasonIds, rescheduleReasonsMap]
  );

  const handleAddItem = (key: string) => {
    const selectedReason = rescheduleReasonsMap[key];
    if (!selectedReason) {
      return;
    }
    const selectedItems = rescheduledReasonIds ?? [];
    const updatedRescheduleReasons = [...selectedItems, key];

    setValue("rescheduledReasonIds", updatedRescheduleReasons, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleRemoveItem = (key: string) => {
    const updatedRescheduledReasonIds = rescheduledReasonIds?.filter(
      (id) => id !== key
    );
    setValue("rescheduledReasonIds", updatedRescheduledReasonIds, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <ConfigAccordionHeader title="Reschedule Reasons">
      {allRescheduleReasons.length === 0 && (
        <MiPlaceholderArea
          text="No Reschedule Reasons Created"
          helperText="Tip: Create your Reschedule Reasons and you can see them here."
          showLogo
          bgColor={theme.palette.primary.light}
        />
      )}
      {allRescheduleReasons.length > 0 && (
        <TransferList
          showAvailableItemsSearch
          availableItems={availableRescheduleReasons}
          selectedItems={selectedRescheduleReasons}
          onAddItem={handleAddItem}
          onRemoveItem={handleRemoveItem}
          availableTitle="Available Reschedule Reasons"
          selectedTitle="Selected Reschedule Reasons"
          disabled={isReadOnly}
        />
      )}
    </ConfigAccordionHeader>
  );
};

interface InspectionTemplateRescheduleReasonsTabProps {
  form: ReturnType<typeof useForm<IInspectionTemplatePayload>>;
  extraTabProps?: {
    isReadOnly?: boolean;
  };
}
